import { useQuery, useQueries } from "@tanstack/react-query";
import { useRef } from "react";
import { apiFetch } from "@/api/fetch";
import type { BlockInfo, FigureInfo, PageImage, PaperData, PaperMeta } from "../types";
import type { CitationRoute } from "@/features/analytics/types/events";

export const paperKeys = {
  all: ["papers"] as const,
  meta: (docId: string) => [...paperKeys.all, "meta", docId] as const,
  detail: (docId: string) => [...paperKeys.all, "detail", docId] as const,
  figures: (docId: string) => [...paperKeys.all, "figures", docId] as const,
  blocks: (docId: string, linesKey: string) =>
    [...paperKeys.all, "blocks", docId, linesKey] as const,
  page: (docId: string, pageNum: number) =>
    [...paperKeys.all, "page", docId, pageNum] as const,
};

export const documentKeys = {
  all: ["documents"] as const,
  detail: (source: CitationRoute, docId: string) =>
    [...documentKeys.all, source, docId] as const,
  fdaBlocks: (docId: string, linesKey: string) =>
    [...documentKeys.all, "fda", "blocks", docId, linesKey] as const,
  fdaPage: (docId: string, pageNum: number) =>
    [...documentKeys.all, "fda", "page", docId, pageNum] as const,
};

async function parseJsonOrThrow<T>(
  response: Response,
  notFoundMessage: string,
  failMessage: string,
): Promise<T> {
  if (!response.ok) {
    throw new Error(response.status === 404 ? notFoundMessage : failMessage);
  }
  return response.json() as Promise<T>;
}

export const fetchPaperMeta = async (
  docId: string,
  signal?: AbortSignal,
): Promise<PaperMeta | null> => {
  const response = await apiFetch(`/api/papers/${docId}/meta`, { signal });
  if (!response.ok) return null;
  const data = (await response.json()) as { meta?: PaperMeta };
  return data.meta ?? null;
};

export const fetchPaper = async (
  docId: string,
  signal?: AbortSignal,
): Promise<PaperData> => {
  const response = await apiFetch(`/api/papers/${docId}`, { signal });
  return parseJsonOrThrow<PaperData>(
    response,
    "Paper not found",
    "Failed to load paper",
  );
};

export const fetchPaperFigures = async (
  docId: string,
  signal?: AbortSignal,
): Promise<FigureInfo[]> => {
  const response = await apiFetch(`/api/papers/${docId}/figures`, { signal });
  if (!response.ok) return [];
  const data = (await response.json()) as { figures?: FigureInfo[] };
  return data.figures ?? [];
};

export const fetchDocument = async (
  source: CitationRoute,
  docId: string,
  highlightLines: number[],
  signal?: AbortSignal,
): Promise<PaperData> => {
  const hlParam =
    highlightLines.length > 0
      ? `?highlights=${highlightLines.join(",")}`
      : "";
  const response = await apiFetch(`/api/${source}/${docId}${hlParam}`, { signal });
  return parseJsonOrThrow<PaperData>(
    response,
    "Document not found",
    "Failed to load document",
  );
};

export const fetchPaperBlocks = async (
  docId: string,
  lineNums: number[],
  signal?: AbortSignal,
): Promise<{ blocks: BlockInfo[]; page_dimensions?: { width: number; height: number } }> => {
  const response = await apiFetch(
    `/api/papers/${docId}/blocks?lines=${lineNums.join(",")}`,
    { signal },
  );
  if (!response.ok) throw new Error("Failed to load block info");
  return response.json();
};

export const fetchPaperPage = async (
  docId: string,
  pageNum: number,
  signal?: AbortSignal,
): Promise<PageImage | null> => {
  const response = await apiFetch(
    `/api/papers/${docId}/page/${pageNum}?scale=1.5`,
    { signal },
  );
  if (!response.ok) return null;
  return response.json();
};

export const fetchFdaBlocks = async (
  docId: string,
  lineNums: number[],
  signal?: AbortSignal,
): Promise<{ blocks: BlockInfo[]; page_dimensions?: { width: number; height: number } }> => {
  const response = await apiFetch(
    `/api/fda/${docId}/blocks?lines=${lineNums.join(",")}`,
    { signal },
  );
  if (!response.ok) throw new Error("Failed to load block info");
  return response.json();
};

export const fetchFdaPage = async (
  docId: string,
  pageNum: number,
  signal?: AbortSignal,
): Promise<PageImage> => {
  const response = await apiFetch(
    `/api/fda/${docId}/page/${pageNum}?scale=1.5`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "PDF page not found"
        : "Failed to load PDF page",
    );
  }
  return response.json();
};

export const usePaperMetaQuery = (docId: string | undefined) =>
  useQuery({
    queryKey: paperKeys.meta(docId ?? ""),
    queryFn: ({ signal }) => fetchPaperMeta(docId!, signal),
    enabled: !!docId,
  });

export const usePaperQuery = (docId: string | undefined) =>
  useQuery({
    queryKey: paperKeys.detail(docId ?? ""),
    queryFn: ({ signal }) => fetchPaper(docId!, signal),
    enabled: !!docId,
  });

export const usePaperFiguresQuery = (docId: string | undefined) =>
  useQuery({
    queryKey: paperKeys.figures(docId ?? ""),
    queryFn: ({ signal }) => fetchPaperFigures(docId!, signal),
    enabled: !!docId,
  });

export const useDocumentQuery = (
  source: CitationRoute,
  docId: string | undefined,
  highlightLines: number[],
) => {
  const highlightLinesRef = useRef(highlightLines);
  highlightLinesRef.current = highlightLines;

  return useQuery({
    queryKey: documentKeys.detail(source, docId ?? ""),
    queryFn: ({ signal }) =>
      fetchDocument(source, docId!, highlightLinesRef.current, signal),
    enabled: !!docId,
  });
};

export const usePaperBlocksQuery = (
  docId: string | undefined,
  lineNums: number[],
) => {
  const linesKey = lineNums.join(",");
  return useQuery({
    queryKey: paperKeys.blocks(docId ?? "", linesKey),
    queryFn: ({ signal }) => fetchPaperBlocks(docId!, lineNums, signal),
    enabled: !!docId && lineNums.length > 0,
  });
};

export const useFdaBlocksQuery = (
  docId: string | undefined,
  lineNums: number[],
) => {
  const linesKey = lineNums.join(",");
  return useQuery({
    queryKey: documentKeys.fdaBlocks(docId ?? "", linesKey),
    queryFn: ({ signal }) => fetchFdaBlocks(docId!, lineNums, signal),
    enabled: !!docId && lineNums.length > 0,
  });
};

export const usePaperPagesQuery = (
  docId: string | undefined,
  pageNums: number[],
) => {
  const results = useQueries({
    queries: pageNums.map((pageNum) => ({
      queryKey: paperKeys.page(docId ?? "", pageNum),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchPaperPage(docId!, pageNum, signal),
      enabled: !!docId,
    })),
  });

  const pageImages = new Map<number, PageImage>();
  pageNums.forEach((pageNum, i) => {
    const img = results[i]?.data;
    if (img) pageImages.set(pageNum, img);
  });

  return {
    pageImages,
    isPending: results.some((r) => r.isPending),
  };
};

export const useFdaPagesQuery = (
  docId: string | undefined,
  pageNums: number[],
) => {
  const results = useQueries({
    queries: pageNums.map((pageNum) => ({
      queryKey: documentKeys.fdaPage(docId ?? "", pageNum),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchFdaPage(docId!, pageNum, signal),
      enabled: !!docId,
    })),
  });

  const pageImages = new Map<number, PageImage>();
  const failedPages = new Set<number>();
  pageNums.forEach((pageNum, i) => {
    const result = results[i];
    const img = result?.data;
    if (img) pageImages.set(pageNum, img);
    if (result?.isError) failedPages.add(pageNum);
  });

  return {
    pageImages,
    failedPages,
    isPending: results.some((r) => r.isPending),
  };
};
